// src/components/site/HeaderNav.tsx
"use client";

import { useEffect, useRef, useState } from "react";

import { NavHighlight } from "./NavHighlight"; // tiny client enhancer
import NavLink from "./NavLink";

// Single source of truth for the primary nav. Rendered by BOTH the desktop bar
// and the mobile menu so the two can never drift. A node is either a leaf link
// or a labelled dropdown group.
type Leaf = { href: string; label: string; external?: boolean };
type Group = { menu: string; label: string; items: Leaf[] };
type NavNode = Leaf | Group;

const isGroup = (n: NavNode): n is Group => "items" in n;

const TOOLS: Leaf[] = [
  { href: "/tools/citation-verifier", label: "Citation Verifier" },
  { href: "/tools/chatgpt-reference-checker", label: "ChatGPT Reference Checker" },
  { href: "/tools/bibliography-audit", label: "Bibliography Audit" },
  { href: "/tools/identifier-detector", label: "Identifier Detector" },
  { href: "/tools/doi-lookup", label: "DOI Lookup" },
  { href: "/tools/pubmed-id-converter", label: "PubMed ID Converter" },
  { href: "/tools/doi-to-bibtex", label: "DOI to BibTeX" },
  { href: "/tools/doi-to-ris", label: "DOI to RIS" },
  { href: "/tools/gb-t-7714-citation-generator", label: "GB/T 7714 Generator" },
  { href: "/tools/ama-citation-generator", label: "AMA Generator" },
  { href: "/tools/vancouver-citation-generator", label: "Vancouver Generator" },
  { href: "/tools/ieee-citation-generator", label: "IEEE Generator" },
  { href: "/tools/acs-citation-generator", label: "ACS Generator" },
  { href: "/tools/cse-citation-generator", label: "CSE Generator" },
  { href: "/tools/open-access-checker", label: "Open Access Checker" },
  { href: "/tools/retraction-checker", label: "Retraction Checker" },
  { href: "/tools/citation-style-comparator", label: "Style Comparator" },
  { href: "/tools/identifier-validator", label: "Identifier Validator" },
  { href: "/tools", label: "All tools" },
];

const USE_CASES: Leaf[] = [
  { href: "/talks", label: "Talk Prep" },
  { href: "/grants", label: "Grants" },
  { href: "/posters", label: "Posters" },
];

const INTEGRATIONS: Leaf[] = [
  { href: "/integrations/zotero", label: "Zotero" },
  { href: "/integrations/obsidian", label: "Obsidian" },
  { href: "/integrations/chatgpt", label: "ChatGPT app" },
  { href: "/mcp#claude-connector", label: "Claude connector" },
  { href: "/mcp", label: "MCP server" },
  { href: "https://github.com/mlava/scholar-sidekick-gemini", label: "Gemini extension" },
  { href: "/integrations/vscode", label: "VS Code" },
  { href: "/extension", label: "Browser extension" },
  { href: "https://github.com/mlava/scholar-sidekick-cli", label: "Command line (CLI)" },
  { href: "https://pypi.org/project/scholar-sidekick/", label: "Python (PyPI)" },
  { href: "/integrations", label: "All integrations" },
];

// Help and Status live in the footer (FooterLegalNav): neither is a first
// action for a visitor, and the bar is the first thing a phone menu shows.
const NAV: NavNode[] = [
  { menu: "tools", label: "Tools", items: TOOLS },
  { menu: "useCases", label: "Use Cases", items: USE_CASES },
  { menu: "integrations", label: "Integrations", items: INTEGRATIONS },
  { href: "/docs", label: "API Docs" },
  { href: "/citation-integrity", label: "Accuracy" },
  { href: "/account", label: "Account" },
];

const LINK_BASE = "text-sm font-medium hover:underline underline-offset-4";
const MENU_ITEM = "block rounded-md px-2 py-2 hover:bg-[hsl(var(--muted))]";

/** Render a leaf — external links open in a new tab; internal use NavLink. */
function NavLeaf({ leaf, className }: { leaf: Leaf; className?: string }) {
  if (leaf.external) {
    return (
      <a
        href={leaf.href}
        target="_blank"
        rel="noopener"
        className={className ? `${LINK_BASE} ${className}` : LINK_BASE}
      >
        {leaf.label}
      </a>
    );
  }
  return (
    <NavLink href={leaf.href} className={className}>
      {leaf.label}
    </NavLink>
  );
}

function Caret({ open }: { open: boolean }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 12 12" className="mr-1 nav-caret">
      <path d={open ? "M2 4l4 4 4-4z" : "M4 2l4 4-4 4z"} fill="currentColor" />
    </svg>
  );
}

export default function HeaderNav() {
  const desktopRef = useRef<HTMLElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);
  // Which desktop dropdown is open (by `menu` key), or null.
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (openMenu && !desktopRef.current?.contains(target)) setOpenMenu(null);
      if (mobileOpen && !mobileRef.current?.contains(target)) setMobileOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpenMenu(null);
      setMobileOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openMenu, mobileOpen]);

  const toggleMenu = (key: string) => setOpenMenu((cur) => (cur === key ? null : key));

  return (
    <div className="flex items-center">
      {/* Desktop bar (md and up) */}
      {/* Both display variants (no base `flex`/`hidden`): each overrides only the
          default display, which this Tailwind v4 build resolves correctly —
          an explicit base display utility would instead win over the variant. */}
      <nav
        ref={desktopRef}
        className="items-center gap-4 md:flex max-md:hidden"
        aria-label="Primary"
        onClickCapture={(event) => {
          if ((event.target as HTMLElement | null)?.closest("a")) setOpenMenu(null);
        }}
      >
        {NAV.map((node) =>
          isGroup(node) ? (
            <div key={node.menu} className="relative z-50">
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={openMenu === node.menu}
                className="cursor-pointer whitespace-nowrap text-sm font-medium text-[hsl(var(--link))] hover:text-[hsl(var(--link-hover))] hover:underline underline-offset-4"
                onClick={() => toggleMenu(node.menu)}
              >
                <Caret open={openMenu === node.menu} />
                {node.label}
              </button>
              <div
                className={`absolute right-0 mt-2 w-56 rounded-lg border border-border bg-bg p-2 shadow transition duration-150 ease-out motion-reduce:transition-none ${
                  openMenu === node.menu
                    ? "block translate-y-0 opacity-100 pointer-events-auto"
                    : "hidden -translate-y-1 opacity-0 pointer-events-none"
                }`}
              >
                {node.items.map((leaf) => (
                  <NavLeaf key={leaf.href} leaf={leaf} className={MENU_ITEM} />
                ))}
              </div>
            </div>
          ) : (
            <NavLeaf key={node.href} leaf={node} />
          ),
        )}

        {/* Post-paint enhancer; no layout impact */}
        <span className="sr-only" aria-hidden="true">
          <NavHighlight />
        </span>
      </nav>

      {/* Mobile menu (below md) */}
      <div className="relative z-50 md:hidden" ref={mobileRef}>
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          className="-mr-1 inline-flex items-center justify-center rounded-md p-2 text-[hsl(var(--link))] hover:bg-[hsl(var(--muted))]"
          onClick={() => setMobileOpen((open) => !open)}
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            {mobileOpen ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M3 6h18M3 12h18M3 18h18" />}
          </svg>
        </button>
        <div
          id="mobile-menu"
          className={`absolute right-0 mt-2 max-h-[80vh] w-64 overflow-y-auto rounded-lg border border-border bg-bg p-2 shadow ${
            mobileOpen ? "block" : "hidden"
          }`}
          onClickCapture={(event) => {
            if ((event.target as HTMLElement | null)?.closest("a")) setMobileOpen(false);
          }}
        >
          {NAV.map((node) =>
            isGroup(node) ? (
              <div key={node.menu} className="mt-1">
                <p className="px-2 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {node.label}
                </p>
                {node.items.map((leaf) => (
                  <NavLeaf key={leaf.href} leaf={leaf} className={MENU_ITEM} />
                ))}
              </div>
            ) : (
              <NavLeaf key={node.href} leaf={node} className={MENU_ITEM} />
            ),
          )}
        </div>
      </div>
    </div>
  );
}
